import { memo, useCallback, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { DbObject, DbStartAtIndex, DbReminder } from "@/db/schema"
import { ObjectType } from "@/db/schema"
import { db } from "@/db/schema"
import { addChecklistItem, checklistParentPath } from "@/db/operations"
import { startTimer } from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { scrollChecklistRowIntoView } from "@/features/task-detail/components/checklist/scrollUtils"
import { normalizeUserStatus } from "@/shared/constants/userStatus"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover"
import { UserStatusPicker } from "@/shared/components/UserStatusPicker"
import { DatePickerSheet } from "@/features/schedule/DatePickerSheet"
import { PlanSheet } from "@/features/plans/PlanSheet"
import { RecurrenceSheet } from "@/features/recurrence"
import { useRecurrence } from "@/features/recurrence"
import CalendarIcon from "@/shared/icons/totodoicons/stroke/calendar.svg?react"
import TagIcon from "@/shared/icons/totodoicons/stroke/tag.svg?react"
import ListCheckIcon from "@/shared/icons/totodoicons/stroke/list-1.svg?react"
import FlagIcon from "@/shared/icons/totodoicons/stroke/flag.svg?react"
import AutomationIcon from "@/shared/icons/pikaicons/stroke/development/automation.svg?react"
import RepeatIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"
import TimerIcon from "@/shared/icons/pikaicons/stroke/time/timer-default.svg?react"
import { shortcutLabel } from "@/shared/utils/platform/shortcutLabel"

interface DetailQuickActionsProps {
  task: DbObject
  startAtData?: DbStartAtIndex
  existingReminder?: DbReminder
  allReminders?: DbReminder[]
  onRequestTagInput?: () => void
}

/**
 * Quick-action icons — v1 CardFullActions pattern.
 *
 * 5 icon buttons on the right side of the bottom date bar.
 * Each hides when its feature is already active:
 * 1. Calendar → hidden if startAt, opens WhenSheet (schedule + reminder + deadline)
 * 2. Tag → hidden if tags exist, shows tag section + focuses input
 * 3. Checklist → hidden if checklist items exist, creates first item
 * 4. Flag → hidden if dueAt, opens DeadlineSheet (calendar-only, sets dueAt)
 * 5. Status → hidden if userStatus set, opens UserStatusPicker popover
 */
export const DetailQuickActions = memo(function DetailQuickActions({
  task,
  startAtData,
  existingReminder,
  allReminders,
  onRequestTagInput,
}: DetailQuickActionsProps) {
  const { t } = useTranslation()
  const id = task.id
  const [showWhen, setShowWhen] = useState(false)
  const [showDeadline, setShowDeadline] = useState(false)
  const [showPlan, setShowPlan] = useState(false)
  const [showRecurrence, setShowRecurrence] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)

  // Memoize shortcut labels (stable per platform)
  const shortcuts = useMemo(
    () => ({
      schedule: shortcutLabel("Mod+S"),
      tag: shortcutLabel("Shift+Mod+T"),
      deadline: shortcutLabel("Shift+Mod+D"),
    }),
    [],
  )

  // Check if checklist items exist
  const parentPath = checklistParentPath(task.parentPath, id ?? "")
  const [checklistCount] = useLiveQueryImmediate(
    () =>
      db.totodo
        .where("[parentPath+type]")
        .equals([parentPath, ObjectType.Checklist])
        .filter((item) => item.isDeleted === 0)
        .count(),
    ["quick-checklist-count", parentPath],
    0,
  )

  // Conditions for hiding icons (v1 pattern: display "none" when active)
  const hasSchedule = !!startAtData?.startAt
  const hasTags = !!(task.tags && task.tags.length > 0)
  const hasChecklist = checklistCount > 0
  const hasFlag = !!task.dueAt
  const hasUserStatus = !!normalizeUserStatus(task.userStatus)
  const hasPlan = !!task.planId || !!task.anchorTaskId
  const recurrenceTemplateId = task.isRecurringTemplate ? id : task.recurringTemplateId
  const recurrence = useRecurrence(recurrenceTemplateId ?? id)
  const hasRecurrence = !!recurrence || !!task.isRecurringTemplate || !!task.recurringTemplateId

  // — Calendar: open WhenSheet (schedule calendar)
  const handleCalendar = useCallback(() => {
    setShowWhen(true)
  }, [])

  // — Tag: show tags section + focus input
  const handleTag = useCallback(() => {
    onRequestTagInput?.()
  }, [onRequestTagInput])

  // — Checklist: create first empty item, then focus it
  const handleChecklist = useCallback(async () => {
    if (!id) return
    const result = await addChecklistItem(id, task.parentPath, "")
    if (result.ok) {
      const newId = result.data.id
      const focusItem = (attempt = 0) => {
        const el = document.querySelector(
          `[data-checklist-id="${newId}"] [contenteditable]`,
        )
        if (el instanceof HTMLElement) {
          // preventScroll + our own engine: the native focus-scroll fights it
          // and collides with the card's transform ancestors (NEVER-20).
          el.focus({ preventScroll: true })
          scrollChecklistRowIntoView(newId)
        } else if (attempt < 20) {
          requestAnimationFrame(() => focusItem(attempt + 1))
        }
      }
      requestAnimationFrame(() => focusItem())
    }
  }, [id, task.parentPath])

  // Auto-focus input when it opens, and scroll into view on mobile
  // (removed — inline input reverted)

  // — Flag: open DeadlineSheet (calendar-only, sets dueAt)
  const handleFlag = useCallback(() => {
    setShowDeadline(true)
  }, [])

  // — Plan: open PlanSheet (smart plan wizard)
  const handlePlan = useCallback(() => {
    setShowPlan(true)
  }, [])

  // — Recurrence: open RecurrenceSheet
  const handleRecurrence = useCallback(() => {
    setShowRecurrence(true)
  }, [])

  // — Timer: start time tracking
  const handleStartTimer = useCallback(() => {
    if (id) void startTimer(id)
  }, [id])

  const iconBtn =
    "rounded-[5px] p-1 text-muted-foreground transition-all duration-[--duration-fast] ease-[--ease-spring] hover:bg-accent"

  return (
    <>
      <div className="flex items-center">
        {/* Calendar — open schedule calendar */}
        {!hasSchedule && (
          <button
            onClick={handleCalendar}
            title={t("Schedule ({{shortcut}})", {
              shortcut: shortcuts.schedule,
            })}
            aria-label={t("Schedule ({{shortcut}})", {
              shortcut: shortcuts.schedule,
            })}
            className={iconBtn}
          >
            <CalendarIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* Tag — focus tag input */}
        {!hasTags && (
          <button
            onClick={handleTag}
            title={t("Add tag ({{shortcut}})", { shortcut: shortcuts.tag })}
            aria-label={t("Add tag ({{shortcut}})", {
              shortcut: shortcuts.tag,
            })}
            className={iconBtn}
          >
            <TagIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* Checklist — create/add checklist item */}
        {!hasChecklist && (
          <button
            onClick={handleChecklist}
            title={t("Add checklist")}
            aria-label={t("Add checklist")}
            className={iconBtn}
          >
            <ListCheckIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* Flag — open deadline calendar */}
        {!hasFlag && (
          <button
            onClick={handleFlag}
            title={t("Add deadline ({{shortcut}})", {
              shortcut: shortcuts.deadline,
            })}
            aria-label={t("Add deadline ({{shortcut}})", {
              shortcut: shortcuts.deadline,
            })}
            className={iconBtn}
          >
            <FlagIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* Plan — open Smart Plan wizard */}
        {!hasPlan && (
          <button
            onClick={handlePlan}
            title={t("Smart Plan")}
            aria-label={t("Smart Plan")}
            className={iconBtn}
          >
            <AutomationIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* Timer — start time tracking */}
        {!task.timerStartedAt && !task.timeSpentMs && (
          <button
            onClick={handleStartTimer}
            title={t("timeTracking.start", "Start timer")}
            aria-label={t("timeTracking.start", "Start timer")}
            className={iconBtn}
          >
            <TimerIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* Recurrence — open RecurrenceSheet */}
        {!hasRecurrence && (
          <button
            onClick={handleRecurrence}
            title={t("recurrence.title", "Repeat")}
            aria-label={t("recurrence.title", "Repeat")}
            className={iconBtn}
          >
            <RepeatIcon className="size-6" aria-hidden="true" />
          </button>
        )}

        {/* UserStatus — dashed circle, opens UserStatusPicker popover */}
        {!hasUserStatus && id && (
          <Popover open={statusOpen} onOpenChange={setStatusOpen}>
            <PopoverTrigger asChild>
              <button
                title={t("Set status")}
                aria-label={t("Set status")}
                className={iconBtn}
              >
                <svg
                  aria-hidden="true"
                  className="size-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10.262 3.017a9.1 9.1 0 0 1 3.476 0m3.384 1.4a9.1 9.1 0 0 1 2.461 2.47m1.4 3.375a9.1 9.1 0 0 1 0 3.476m-1.4 3.384a9.1 9.1 0 0 1-2.47 2.46m-3.375 1.4a9.1 9.1 0 0 1-3.476 0m-3.384-1.4a9.1 9.1 0 0 1-2.46-2.47m-1.4-3.374a9.1 9.1 0 0 1 0-3.476m1.4-3.385a9.1 9.1 0 0 1 2.47-2.46"
                  />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              side="top"
              className="flex w-56 flex-col overflow-hidden p-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <UserStatusPicker
                taskId={id}
                currentValue={task.userStatus}
                onClose={() => setStatusOpen(false)}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Schedule picker (Calendar icon) */}
      {id && (
        <DatePickerSheet
          open={showWhen}
          onOpenChange={setShowWhen}
          task={task}
          mode="schedule"
          startAtData={startAtData}
          existingReminder={existingReminder}
          allReminders={allReminders}
        />
      )}

      {/* Deadline picker (Flag icon) */}
      {id && (
        <DatePickerSheet
          open={showDeadline}
          onOpenChange={setShowDeadline}
          task={task}
          mode="deadline"
        />
      )}

      {/* Smart Plan wizard */}
      {id && (
        <PlanSheet
          open={showPlan}
          onOpenChange={setShowPlan}
          task={task}
        />
      )}

      {/* Recurrence sheet */}
      {id && (
        <RecurrenceSheet
          open={showRecurrence}
          onOpenChange={setShowRecurrence}
          task={task}
          startAtData={startAtData}
        />
      )}
    </>
  )
})
